import { BRAND } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";
import { MapPin } from "lucide-react";

export default function Markets() {
    const { t } = useI18n();
    const scrollToContact = (marketId) => {
        // Persist market pre-selection for the enquiry form
        try {
            sessionStorage.setItem("luxenova.market", marketId);
        } catch (_) { /* ignore */ }
        document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
        window.dispatchEvent(new CustomEvent("luxenova:preselect-market", { detail: marketId }));
    };

    return (
        <section id="markets" data-testid="markets-section" className="relative py-28 md:py-36 bg-gradient-to-b from-[#050D1C] via-[#07142B] to-[#050D1C]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal max-w-3xl">
                    <span className="eyebrow">{t("markets.eyebrow")}</span>
                    <h2 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
                        {t("markets.title_a")} <em className="not-italic text-[#C9A96E]">{t("markets.title_highlight")}</em>
                    </h2>
                    <p className="mt-8 text-slate-400 max-w-2xl leading-relaxed">{t("markets.subtitle")}</p>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
                    {BRAND.markets.map((m, i) => {
                        const c = t(`markets.countries.${m.id}`) || {};
                        const svcLabels = t("markets.service_labels") || {};
                        return (
                            <article
                                key={m.id}
                                data-testid={`market-card-${m.id}`}
                                className="reveal glass-card overflow-hidden group flex flex-col"
                                style={{ transitionDelay: `${i * 60}ms` }}
                            >
                                <div className="relative h-52 overflow-hidden">
                                    <img
                                        src={m.image}
                                        alt={c.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#050D1C] via-[#050D1C]/40 to-transparent" />
                                    <div className="absolute top-4 left-4 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase bg-[#050D1C]/60 backdrop-blur-md px-3 py-1.5 border border-[#C9A96E]/30 text-[#C9A96E]">
                                        <MapPin size={11} strokeWidth={1.6} /> {c.tag}
                                    </div>
                                </div>
                                <div className="p-7 flex flex-col flex-1">
                                    <h3 className="font-serif-display text-white text-2xl">{c.name}</h3>
                                    <p className="mt-3 text-slate-400 text-sm leading-relaxed">{c.blurb}</p>
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        {m.services.map((s) => (
                                            <span
                                                key={s}
                                                className="text-[9px] tracking-[0.24em] uppercase px-2.5 py-1 border border-[#C9A96E]/25 text-slate-300"
                                            >
                                                {svcLabels[s] || s}
                                            </span>
                                        ))}
                                    </div>
                                    <button
                                        data-testid={`market-cta-${m.id}`}
                                        onClick={() => scrollToContact(m.id)}
                                        className="mt-6 pt-5 border-t border-[#C9A96E]/15 text-[11px] tracking-[0.28em] uppercase text-[#C9A96E] hover:text-white transition-colors text-left"
                                    >
                                        {t("markets.cta")} →
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
